import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { CalculatedAmount } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/CalculatedAmount/CalculatedAmount";
import { BillTotalAmountForm } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillTotalCell/components/BillTotalAmountForm/BillTotalAmountForm";
import { JointBillContext } from "@features/JointBillList/utils";
import { Dropdown } from "@components/Dropdown/Dropdown";
import { IconButton } from "@components/IconButton/IconButton";
import {
  BinIcon,
  DocumentArrowIcon,
  DocumentCheckIcon,
  DocumentIcon,
  DotsHorizontalIcon,
  Pencil16Icon,
  PencilIcon,
  ReceiptIcon,
  RightLeftArrowIcon,
  ShieldIcon,
  ShieldStrikethroughIcon,
} from "@components/icons";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { Tooltip } from "@components/Tooltip/Tooltip";
import {
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import { formatPrice } from "@utils/units";

interface IProviderBillTotalCellProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  billId?: IBillData["billId"];
  totalSum?: number;
  totalAmount?: number;
  currency?: string;
  isCheckboxChecked?: boolean;
  isCheckboxDisabled?: boolean;
  isCheckboxLoading?: boolean;
  onCheckboxClick?: () => void;
  isTotalAmountEditable?: boolean;
  isProviderEditButtonEnabled?: boolean;
  onEditProviderClick?: () => void;
  isProviderRemoveButtonEnabled?: boolean;
  onRemoveProviderClick?: () => void;
  onProviderBillClick?: () => void;
  isProviderBillButtonEnabled?: boolean;
  onProviderBookletClick?: () => void;
  isProviderBookletButtonEnabled?: boolean;
  shouldHideCheckbox?: boolean;
  isBillFileAvailable?: boolean;
  isBillFileLoading?: boolean;
  onBillFileClick?: () => void;
  isBillCarryOverEnabled?: boolean;
  onBillCarryOverClick?: () => void;
  isChubbBillInsuranceEnabled?: boolean;
  isChubbBillInsuranceAvailable?: boolean;
  isChubbInsuranceFilesLoading?: boolean;
  onChubbInsuranceClick?: () => void;
  isCertFileAvailable?: boolean;
  isCertFileLoading?: boolean;
  onCertFileClick?: () => void;
  isTransferAvailable?: boolean;
  onTransferClick?: () => void;
}

export const ProviderBillTotalCell: React.FC<IProviderBillTotalCellProps> = ({
  jointBillServiceId,
  periodId,
  billId,
  totalSum,
  totalAmount,
  currency,
  isCheckboxChecked,
  isCheckboxDisabled,
  isCheckboxLoading,
  onCheckboxClick,
  isTotalAmountEditable,
  isProviderEditButtonEnabled,
  onEditProviderClick,
  isProviderRemoveButtonEnabled,
  onRemoveProviderClick,
  onProviderBillClick,
  isProviderBillButtonEnabled,
  onProviderBookletClick,
  isProviderBookletButtonEnabled,
  shouldHideCheckbox,
  isBillFileAvailable,
  isBillFileLoading,
  onBillFileClick,
  isBillCarryOverEnabled,
  onBillCarryOverClick,
  isChubbBillInsuranceEnabled,
  isChubbBillInsuranceAvailable,
  isChubbInsuranceFilesLoading,
  onChubbInsuranceClick,
  isCertFileAvailable,
  isCertFileLoading,
  onCertFileClick,
  isTransferAvailable,
  onTransferClick,
}) => {
  const { t } = useTranslation();
  const [isTotalAmountFormOpen, setIsTotalAmountFormOpen] = useState(false);
  const { isSummaryMode } = useContext(JointBillContext);

  const toggleTotalAmountForm = () =>
    setIsTotalAmountFormOpen(!isTotalAmountFormOpen);

  const dropdownButton = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (props) => (
      <IconButton
        icon={DotsHorizontalIcon}
        iconClassName="text-base-black"
        {...props}
      />
    ),
    [],
  );

  const dropdownOptions = [
    ...(isProviderEditButtonEnabled
      ? [
          {
            label: t("bill.interactions.editProvider.label"),
            onClick: onEditProviderClick,
            icon: PencilIcon,
          },
        ]
      : []),
    ...(isBillFileAvailable
      ? [
          {
            label: t("bill.interactions.downloadBillFile.label"),
            onClick: onBillFileClick,
            icon: DocumentIcon,
            disabled: isBillFileLoading,
          },
        ]
      : []),
    ...(isTransferAvailable
      ? [
          {
            label: t("bill.interactions.transfer.label"),
            onClick: onTransferClick,
            icon: DocumentArrowIcon,
          },
        ]
      : []),
    ...(isCertFileAvailable
      ? [
          {
            label: t("bill.interactions.downloadCertFile.label"),
            onClick: onCertFileClick,
            icon: DocumentIcon,
            disabled: isCertFileLoading,
          },
        ]
      : []),
    ...(isProviderRemoveButtonEnabled
      ? [
          {
            label: t("bill.interactions.removeProvider.label"),
            onClick: onRemoveProviderClick,
            icon: BinIcon,
          },
        ]
      : []),
    ...(isBillCarryOverEnabled
      ? [
          {
            label: t("bill.interactions.billCarryOver.label"),
            onClick: onBillCarryOverClick,
            icon: RightLeftArrowIcon,
          },
        ]
      : []),
    ...(isProviderBillButtonEnabled
      ? [
          {
            label: t("bill.interactions.providerBill.label"),
            onClick: onProviderBillClick,
            icon: ReceiptIcon,
          },
        ]
      : []),
    ...(isProviderBookletButtonEnabled
      ? [
          {
            label: t("bill.interactions.providerBooklet.label"),
            onClick: onProviderBookletClick,
            icon: DocumentCheckIcon,
          },
        ]
      : []),
    ...(isChubbBillInsuranceEnabled
      ? [
          {
            label: t(
              `bill.interactions.chubbInsurance.${isChubbBillInsuranceAvailable ? "available" : "unavailable"}.label`,
            ),
            onClick: isChubbBillInsuranceAvailable
              ? onChubbInsuranceClick
              : undefined,
            icon: isChubbBillInsuranceAvailable
              ? ShieldIcon
              : ShieldStrikethroughIcon,
            disabled:
              !isChubbBillInsuranceAvailable || isChubbInsuranceFilesLoading,
          },
        ]
      : []),
  ];

  return (
    <div className="relative flex flex-col items-end gap-2 md:mt-0 md:flex-row-reverse md:items-center md:gap-3.5">
      {isTotalAmountFormOpen &&
        !isNullOrUndefined(billId) &&
        !isNullOrUndefined(totalAmount) && (
          <BillTotalAmountForm
            jointBillServiceId={jointBillServiceId}
            periodId={periodId}
            billId={billId}
            amount={totalAmount}
            onClose={toggleTotalAmountForm}
          />
        )}

      <div
        className={twMerge(
          "flex items-center gap-3 md:gap-4",
          isTotalAmountFormOpen && "invisible",
        )}
      >
        {!isSummaryMode && dropdownOptions.length > 0 && (
          <div className="md:hidden">
            <Dropdown
              buttonAs={dropdownButton}
              options={dropdownOptions}
              placement="bottom-end"
              shouldTruncateOptions={!isChubbBillInsuranceEnabled}
            />
          </div>
        )}
        {!shouldHideCheckbox && !isSummaryMode && (
          <Checkbox
            checked={isCheckboxChecked}
            disabled={isCheckboxDisabled}
            onChange={onCheckboxClick}
            isLoading={isCheckboxLoading}
            aria-label={t("label.billCheckbox")}
          />
        )}
      </div>
      <div
        className={twMerge(
          "flex items-center gap-4",
          isTotalAmountFormOpen && "invisible",
        )}
      >
        {!isNullOrUndefined(totalAmount) &&
          !isNullOrUndefined(currency) &&
          !isNullOrUndefined(totalSum) && (
            <div className="flex gap-4">
              <div className="hidden md:block">
                <CalculatedAmount
                  totalAmount={totalAmount}
                  currency={currency}
                  totalSum={totalSum}
                />
              </div>
              <div className="flex gap-1.5 md:gap-2">
                {!isNullOrUndefined(totalAmount) && (
                  <React.Fragment>
                    {isTotalAmountEditable && !isSummaryMode && (
                      <Tooltip
                        content={t("bill.interactions.editAmount.tooltip")}
                      >
                        {(props) => (
                          <IconButton
                            {...props}
                            icon={Pencil16Icon}
                            onClick={toggleTotalAmountForm}
                            aria-label={t(
                              "bill.interactions.editAmount.tooltip",
                            )}
                          />
                        )}
                      </Tooltip>
                    )}
                    <span className="text-sm font-semibold md:text-base">
                      {formatPrice(totalAmount, {
                        currency: currency,
                      })}
                    </span>
                  </React.Fragment>
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
